import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Slide,
} from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import React from 'react'

import { FooterContents, type FooterProps } from '../../../layout/footer/Footer'

import { useCustomDialogStyles } from './CustomDialog.styles'
import { CustomDialogTitle } from './custom-title/CustomDialogTitle'

export interface CustomDialogProps {
  title: string
  headerDescription?: string
  description?: string
  content?: JSX.Element
  hasYBorders?: boolean
  buttons?: FooterProps
  hideCloseButton?: boolean
  children?: React.ReactNode
  isOpen: boolean
  onCloseDialog: () => void
  fullHeight?: boolean
  titleNoWrap?: boolean
  capitalizeTitle?: boolean
  'data-testid'?: string
  disableEscapeKeydown?: boolean
  disableBackdropClick?: boolean
}

const UpTransition = React.forwardRef(
  (
    props: TransitionProps & { children: React.ReactElement<unknown> },
    ref: React.Ref<unknown>,
  ) => {
    return (
      <Slide direction="up" ref={ref} {...props}>
        {props.children}
      </Slide>
    )
  },
)

export const CustomDialog = ({
  title,
  headerDescription,
  description,
  content,
  hasYBorders,
  buttons,
  hideCloseButton = false,
  isOpen = false,
  onCloseDialog,
  fullHeight = false,
  titleNoWrap = false,
  capitalizeTitle = true,
  'data-testid': dataTestId,
  disableEscapeKeydown = false,
  disableBackdropClick = false,
}: CustomDialogProps): JSX.Element => {
  const { cx, classes } = useCustomDialogStyles()

  const handleClose = (_ = {}, reason?: 'escapeKeyDown' | 'backdropClick') => {
    if (reason === 'backdropClick' && disableBackdropClick) {
      return
    }
    if (reason === 'escapeKeyDown' && disableEscapeKeydown) {
      return
    }
    if (onCloseDialog) {
      onCloseDialog()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      TransitionComponent={UpTransition}
      data-testid={dataTestId}
      classes={{
        paper: cx(classes.paper, { [classes.fullHeight]: fullHeight }),
      }}
      disableEscapeKeyDown={disableEscapeKeydown}
    >
      <CustomDialogTitle
        onClose={hideCloseButton ? undefined : () => handleClose()}
        noWrap={titleNoWrap}
        capitalizeTitle={capitalizeTitle}
      >
        {title}
      </CustomDialogTitle>

      {headerDescription && (
        <DialogContentText classes={{ root: classes.contentText }}>
          {headerDescription}
        </DialogContentText>
      )}

      {content && (
        <DialogContent
          classes={{
            root: cx(classes.content, {
              [classes.hasYBorders]: hasYBorders,
            }),
          }}
        >
          {content}
        </DialogContent>
      )}

      {description && (
        <DialogContentText classes={{ root: classes.contentText }}>
          {description}
        </DialogContentText>
      )}

      {buttons && (
        <DialogActions classes={{ root: classes.actions }}>
          <FooterContents {...buttons} />
        </DialogActions>
      )}
    </Dialog>
  )
}
